import {ExistFilter, InFilter, OperatorFilter, TermFilter} from '@dofleini/query-builder';
import {CubaCIRuler, LatterAndNumberRuler} from '@dofleini/validator';
import {proposalrequest} from '@mep/pae-commons';
import {Divider} from 'antd';
import i18n from 'i18next';
import React from 'react';

import Editor from '@/components/Editor';
import FormTitleSession from '@/components/FormTitleSession';
import InfoBox from '@/components/InfoBox';
import BankOfficeSelect from '@/modules/configuration/components/BankOfficeSelect';
import BankTypeSelect from '@/modules/configuration/components/BankTypeSelect';
import CNAESelect from '@/modules/configuration/components/CNAESelect';
import ProposalActivitiesSelect from '@/modules/proposalRequest/components/ProposalActivitiesSelect';
import ProposalTrademark from '@/modules/proposalRequest/components/ProposalTrademark';
import QuestionWithDetails from '@/modules/proposalRequest/components/QuestionWithDetails';
import {validations} from '@/utils/fields';

const {SUBJECT_TYPE} = proposalrequest;

export const representationFields = [
  {
    field: 'option1',
    label: 'proposalRequest.permissions.representation.selectCause',
    type: 'select',
    required: true,
    placeholder: 'selectPlaceholder',
    options: [
      {label: 'causes.exterior_residence', value: 'exterior_residence'},
      {label: 'causes.invalid_by_law', value: 'invalid_by_law'},
      {label: 'causes.minor_of_age', value: 'minor_of_age'},
    ]
  },
  {field: 'option2', type: 'text', required: true},
  {field: 'option3', type: 'text', required: true},
  {field: 'option4', type: 'text', required: false},
  {field: 'option5', type: 'text', required: false},
];

export const posterFields = [
  {field: 'option1', required: false, type: 'number'},
  {field: 'option2', required: false, type: 'number'},
  {field: 'option3', required: false},
  {field: 'option4', required: false, type: 'file'}
];

export const getProjectFields = (t, proposal, settings, activities, mainActivity, bankType, bankOffice, location, permissions_vehiclerent, permissions_houselease) => {
  let fields = [
    {
      key: 'documentation2',
      colSpan: 3,
      // eslint-disable-next-line react/display-name
      render: () => <FormTitleSession translate={'proposalRequest'} title={'steps.project'} divider/>
    },
    {
      key: 'projectDescription',
      label: t('proposalRequest.form.general.projectDescription.label'),
      widget: Editor,
      colSpan: 3,
      widgetProps: {size: 'large'},
      required: true,
      extra: <InfoBox title={'proposalRequest.form.general.projectDescription.help'}
        translate={'proposalRequest'}/>,
      className: 'form-view-mode-vertical-item'
    },
    {
      key: 'socialObject',
      label: t('proposalRequest.form.general.socialObject.label'),
      widget: Editor,
      colSpan: 3,
      widgetProps: {size: 'large'},
      required: true,
      className: 'form-view-mode-vertical-item'
    },
    {
      key: 'mainActivity',
      colSpan: 3,
      label: t('proposalRequest.form.general.mainActivity.label'),
      placeholder: t('selectPlaceholder'),
      required: true,
      dependencies: ['activities'],
      widget: CNAESelect,
      widgetProps: {
        size: 'large',
        filters: new OperatorFilter({
          type: 'AND',
          filters: [
            new TermFilter({field: 'isProhibited', value: false}),
            new OperatorFilter({
              type: 'OR',
              filters: [
                new ExistFilter({field: 'applyFor', value: false}),
                new InFilter({field: 'applyFor', value: [proposal?.subjectType]})
              ]
            })
          ]
        }).toQuery(),
        ignoreValues: activities
      },
    },
    {
      key: 'otherActivities',
      label: t('proposalRequest.form.general.activities.textLabel'),
      colSpan: 3,
      widget: Editor,
      widgetProps: {size: 'large'},
      className: 'form-view-mode-vertical-item'
    },
    {
      key: 'activities',
      colSpan: 3,
      label: t('proposalRequest.form.general.activities.label'),
      placeholder: t('selectPlaceholder'),
      required: false,
      dependencies: ['mainActivity'],
      widget: ProposalActivitiesSelect,
      extra: <InfoBox title={'proposalRequest.form.general.activities.help'} translate={'proposalRequest'}/>,
      widgetProps: {
        filters: new OperatorFilter({
          type: 'AND',
          filters: [
            new TermFilter({field: 'isProhibited', value: false}),
            new OperatorFilter({
              type: 'OR',
              filters: [
                new ExistFilter({field: 'applyFor', value: false}),
                new InFilter({field: 'applyFor', value: [proposal?.subjectType]})
              ]
            })
          ]
        }).toQuery(),
        mainActivity: mainActivity,
        size: 'large'
      },
    },
    {
      key: 'socialDenomination',
      colSpan: 3,
      label: t('proposalRequest.form.general.name.labelTCP'),
      placeholder: t('defaultPlaceholder'),
      hasFeedback: true,
      required: false,
      widgetProps: {
        size: 'large', type: 'text',
      },
      rules: [!proposal?._id && LatterAndNumberRuler, validations.bannedWordsCheck(settings)]
    },
    {
      key: 'haveTradeMark',
      colSpan: 3,
      placeholder: 'defaultPlaceholder',
      required: false,
      widget: ProposalTrademark,
      widgetProps: {
        label: 'proposalRequest.form.general.haveTradeMark.label',
        size: 'large',
        translate: 'proposalRequest',
        type: 'text',
      },
      rules: [{
        validator: (rule, value) => {
          return new Promise((resolve, reject) => {
            if (value?.active && !value?.file) reject(new Error(i18n.t('validateMessages.required'))); else {
              resolve();
            }
          });
        }
      },]
    },
    {
      key: 'divider', colSpan: 3, // eslint-disable-next-line react/display-name
      render: () => <Divider className="my-3"/>
    },
    {
      label: t('proposalRequest.form.partners.bankType.label'),
      key: 'bankType',
      required: true,
      dependencies: ['address.province'],
      widget: BankTypeSelect,
      colSpan: 3,
      extra: <InfoBox title={'proposalRequest.form.partners.bankType.helpTCP'} translate={'proposalRequest'}/>,
      widgetProps: {
        size: 'large', disabled: !!bankOffice, province: location?.province
      }
    },
    {
      label: t('proposalRequest.form.partners.bankOffice.label'),
      key: 'bankOffice',
      required: true,
      widget: BankOfficeSelect,
      colSpan: 3,
      dependencies: ['bankType'],
      extra: <InfoBox title={'proposalRequest.form.partners.bankOffice.helpTCP'} translate={'proposalRequest'}
        translateValues={{
          type: t(`types.${proposal?.subjectType}`)
        }}/>,
      widgetProps: {
        disabled: !bankType,
        bankType: bankType,
        size: 'large',
        filters: new OperatorFilter({
          type: 'AND',
          filters: [
            new TermFilter({field: 'bank.code', value: bankType}),
            new TermFilter({field: 'address.province', value: location?.province}),
            new InFilter({field: 'applyFor', value: [SUBJECT_TYPE.TCP_PROPOSAL]})
          ]
        }).toQuery(),
      },
    },
    {
      key: 'documentation3',
      colSpan: 3,
      // eslint-disable-next-line react/display-name
      render: () => <FormTitleSession translate={'proposalRequest'} title={'proposalRequest.permissions.label'}
        divider/>
    },
    {
      key: 'permissions_houselease',
      widget: QuestionWithDetails,
      colSpan: 3,
      widgetProps: {
        label: 'proposalRequest.permissions.houselease.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.houselease',
        size: 'large',
        options: [
          {
            field: 'option1',
            label: 'proposalRequest.permissions.houselease.select',
            type: 'select',
            required: true,
            placeholder: 'selectPlaceholder',
            options: [
              {label: 'causes.full_house_lease', value: 'full_house_lease'},
              {label: 'causes.partially_house_lease', value: 'partially_house_lease'},
            ]
          },
          {field: 'option2', type: 'number', required: true},
          {
            field: 'option3', type: 'textarea',
            help: <InfoBox title={'proposalRequest.permissions.houselease.help'}
              translate={'proposalRequest'}/>
          }
        ],
        optionsDirection: 'row'
      }
    },
    {
      key: 'permissions_representation_lease',
      widget: QuestionWithDetails,
      colSpan: 3,
      dependencies: ['permissions_houselease'],
      widgetProps: {
        label: 'proposalRequest.permissions.representation.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.representation',
        size: 'large',
        options: representationFields,
      }
    },
    {
      key: 'permissions_poolsRent',
      widget: QuestionWithDetails,
      colSpan: 3,
      widgetProps: {
        label: 'proposalRequest.permissions.poolsRent.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.poolsRent',
        size: 'large',
        options: [
          {field: 'option1', required: true, type: 'number'},
          {
            field: 'option2',
            required: true,
            type: 'file'
          },
        ]
      }
    },
    {
      key: 'divider', colSpan: 3, // eslint-disable-next-line react/display-name
      render: () => <Divider className="my-0 mb-3"/>
    },
    {
      key: 'permissions_vehiclerent',
      widget: QuestionWithDetails,
      colSpan: 3,
      widgetProps: {
        label: 'proposalRequest.permissions.vehiclerent.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.vehiclerent',
        size: 'large',
        options: [
          {field: 'option1', type: 'boolean', placeholder: 'checkPlaceholder'},
          {field: 'option2', type: 'boolean', placeholder: 'checkPlaceholder'},
          {field: 'option3', type: 'boolean', placeholder: 'checkPlaceholder'},
          {field: 'option4', type: 'textarea'}
        ],
        optionsDirection: 'row'
      }
    },
    {
      key: 'permissions_representation_transport',
      widget: QuestionWithDetails,
      colSpan: 3,
      dependencies: ['permissions_vehiclerent'],
      widgetProps: {
        label: 'proposalRequest.permissions.representation.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.representation',
        size: 'large',
        options: representationFields,
      }
    },
    {
      key: 'permissions_transport', widget: QuestionWithDetails, colSpan: 3,
      widgetProps: {
        label: 'proposalRequest.permissions.transport.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.transport',
        size: 'large',
        options: [
          {field: 'option1', type: 'text'},
          {field: 'option2', type: 'text', validation: CubaCIRuler},
        ],
      }
    },
    {
      key: 'divider', colSpan: 3, // eslint-disable-next-line react/display-name
      render: () => <Divider className="my-0 mb-3"/>
    },
    {
      key: 'permissions_music',
      widget: QuestionWithDetails,
      colSpan: 3,
      widgetProps: {
        label: 'proposalRequest.permissions.music.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.music',
        size: 'large',
        optionsDirection: 'row',
        options: [
          {field: 'option1', type: 'boolean'},
          {field: 'option2', type: 'boolean'},
          {field: 'option3', type: 'boolean'},
          {field: 'option4', type: 'boolean'},
        ],
      }
    },
    {
      key: 'permissions_posters',
      widget: QuestionWithDetails,
      colSpan: 3,
      widgetProps: {
        label: 'proposalRequest.permissions.posters.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.posters',
        size: 'large',
        options: posterFields,
      }
    },
    {
      key: 'permissions_ecommerce', widget: QuestionWithDetails, colSpan: 3, widgetProps: {
        label: 'proposalRequest.permissions.ecommerce.label',
        translate: 'proposalRequest',
        extraLabel: 'proposalRequest.permissions.ecommerce.help',
        size: 'large'
      }
    },
  ];
  if (!permissions_houselease?.active) {
    fields = fields?.filter(e => e?.key !== 'permissions_representation_lease');
  }
  
  if (!permissions_vehiclerent?.active) {
    fields = fields?.filter(e => e?.key !== 'permissions_representation_transport');
  }
  
  return fields;
};
